"use client";

/**
 * ExitIntentPopup — verschijnt 1x per sessie wanneer:
 *   - Desktop: muis verlaat de viewport via de bovenkant (uitlogeren / tab-switch)
 *   - Mobile: bezoeker scrolt agressief omhoog na 30s op pagina (idle-trigger)
 *
 * Doel: laatste-kans-conversie. Toont de actuele promo-actie ("Gratis meubelset")
 * met een sterke CTA naar #brochure-form.
 *
 * Sessie-flag in sessionStorage zodat 'ie niet meerdere keren verschijnt.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { pushDataLayer } from "../lib/gtm";

const STORAGE_KEY = "bw_exit_intent_seen_v1";

// dataLayer event-namen — symmetrisch met HansKazanPopup (zwaluw).
// Vergelijkbaar dashboard zelf-tracking per brand mogelijk via popup + brand.
const POPUP_NAME = "bw_exit_intent";
const POPUP_BRAND = "bw";

export interface ExitIntentContent {
  /** Hoofdtekst van de popup, bv. "Wacht! Vergeet uw cadeau niet" */
  headline?: string;
  /** Sub-text, mag DKI {city} bevatten */
  subheadline?: string;
  /** Actienaam (komt uit promo_actions), bv. "Gratis badkamermeubel..." */
  promo_label?: string;
  /** Waarde-label, bv. "t.w.v. € 2.599" */
  value_label?: string;
  /** CTA-button tekst */
  cta_text?: string;
  /** Anker op de pagina waar de form staat */
  cta_href?: string;
}

interface Props {
  content?: ExitIntentContent;
  /** Default true. Op false zetten om popup volledig uit te schakelen */
  enabled?: boolean;
}

export default function ExitIntentPopup({ content = {}, enabled = true }: Props) {
  const [open, setOpen] = useState(false);
  const [closed, setClosed] = useState(false);
  // Tijdstip waarop popup verscheen — voor time_open_ms berekening bij close.
  const openedAtRef = useRef<number | null>(null);

  const headline = content.headline ?? "Wacht! Vergeet uw exclusieve actie niet";
  const subheadline =
    content.subheadline ??
    "Vraag in 30 seconden uw gratis brochure aan en profiteer van onze huidige actie.";
  const promoLabel = content.promo_label ?? "Gratis cadeau bij uw nieuwe badkamer";
  const valueLabel = content.value_label ?? "";
  const ctaText = content.cta_text ?? "Claim mijn cadeau →";
  const ctaHref = content.cta_href ?? "#brochure-form";

  // method = wat sluit de popup: 'x' / 'backdrop' / 'esc' / 'cta'
  const close = useCallback((method: "x" | "backdrop" | "esc" | "cta" = "x") => {
    const timeOpenMs = openedAtRef.current ? Date.now() - openedAtRef.current : 0;
    pushDataLayer({
      event: method === "cta" ? "popup_cta_click" : "popup_close",
      popup: POPUP_NAME,
      brand: POPUP_BRAND,
      close_method: method,
      time_open_ms: timeOpenMs,
    });
    setOpen(false);
    setClosed(true);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  // show() pakt openedAtRef + dataLayer-event impressions
  const show = useCallback(
    (trigger: "mouseleave" | "mobile_idle") => {
      if (closed) return;
      openedAtRef.current = Date.now();
      setOpen(true);
      pushDataLayer({
        event: "popup_shown",
        popup: POPUP_NAME,
        brand: POPUP_BRAND,
        trigger,
      });
    },
    [closed],
  );

  useEffect(() => {
    if (!enabled) return;
    // Skip als al gezien
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {
      /* sessie zonder storage — gewoon doorgaan */
    }

    // Desktop: mouseleave naar bovenkant
    const onMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) show("mouseleave");
    };

    // Mobile: na 40s idle (geen scroll/touch), trigger 1x
    let mobileTimer: number | undefined;
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
    if (isMobile) {
      mobileTimer = window.setTimeout(() => show("mobile_idle"), 40_000);
    }

    document.addEventListener("mouseleave", onMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, [enabled, show]);

  // Escape-toets sluit modal
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") close("esc");
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="exit-popup-title"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(20,28,40,0.65)",
        display: "grid",
        placeItems: "center",
        padding: 16,
        fontFamily: "system-ui, -apple-system, sans-serif",
        animation: "bwExitFade 200ms ease-out",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) close("backdrop");
      }}
    >
      <style>{`
        @keyframes bwExitFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bwExitSlide { from { opacity: 0; transform: translateY(20px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: 16,
          maxWidth: 480,
          width: "100%",
          padding: "32px 28px 28px",
          position: "relative" as const,
          boxShadow: "0 24px 60px -16px rgba(20,28,40,0.4)",
          animation: "bwExitSlide 240ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Close button */}
        <button
          onClick={() => close("x")}
          aria-label="Sluiten"
          style={{
            position: "absolute" as const,
            top: 14,
            right: 14,
            width: 32,
            height: 32,
            borderRadius: 999,
            border: "none",
            background: "#F3F2EE",
            color: "#5A5550",
            fontSize: 18,
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
            transition: "background 150ms",
          }}
          onMouseOver={(e) => ((e.currentTarget as HTMLElement).style.background = "#E5E3DD")}
          onMouseOut={(e) => ((e.currentTarget as HTMLElement).style.background = "#F3F2EE")}
        >
          ✕
        </button>

        {/* Eyebrow */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "#FFF2E8",
            color: "#FF6B1A",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase" as const,
            padding: "5px 11px",
            borderRadius: 999,
            marginBottom: 14,
          }}
        >
          <span aria-hidden>🎁</span> Exclusieve actie
        </div>

        {/* Headline */}
        <h2
          id="exit-popup-title"
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: "clamp(22px, 3.5vw, 26px)",
            fontWeight: 600,
            color: "#141C28",
            margin: "0 0 10px",
            lineHeight: 1.2,
            letterSpacing: "-0.015em",
          }}
        >
          {headline}
        </h2>

        {/* Subheadline */}
        <p
          style={{
            fontSize: 14.5,
            color: "#5A5550",
            lineHeight: 1.55,
            margin: "0 0 16px",
          }}
        >
          {subheadline}
        </p>

        {/* Promo box */}
        <div
          style={{
            background: "#FFF9F4",
            border: "1px solid #F0E2D2",
            borderRadius: 10,
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20,
          }}
        >
          <span
            style={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: 8,
              background: "#FF6B1A",
              color: "white",
              display: "grid",
              placeItems: "center",
              fontSize: 18,
            }}
            aria-hidden
          >
            🎁
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#141C28", lineHeight: 1.3 }}>
              {promoLabel}
            </div>
            {valueLabel ? (
              <div style={{ fontSize: 11.5, color: "#5A5550", marginTop: 2 }}>{valueLabel}</div>
            ) : null}
          </div>
        </div>

        {/* CTA button */}
        <a
          href={ctaHref}
          onClick={() => close("cta")}
          style={{
            display: "block",
            background: "#FF6B1A",
            color: "white",
            fontSize: 16,
            fontWeight: 600,
            textAlign: "center" as const,
            padding: "14px 20px",
            borderRadius: 10,
            textDecoration: "none",
            boxShadow: "0 6px 16px -8px rgba(255,107,26,0.5)",
            transition: "background 150ms",
          }}
          onMouseOver={(e) => ((e.currentTarget as HTMLElement).style.background = "#E55A0F")}
          onMouseOut={(e) => ((e.currentTarget as HTMLElement).style.background = "#FF6B1A")}
        >
          {ctaText}
        </a>

        {/* Tiny disclaimer */}
        <p
          style={{
            fontSize: 11.5,
            color: "#9A958A",
            textAlign: "center" as const,
            margin: "12px 0 0",
            lineHeight: 1.4,
          }}
        >
          Gratis · 30 seconden · Geen verplichtingen
        </p>
      </div>
    </div>
  );
}
